import React from 'react';
import PropTypes from 'prop-types';
import { ProgressSurveyAnswer, ProgressSurveyQuestion } from 'utils/types';
import DesktopCommentsRow from './DesktopCommentsRow';
import MainRow from './MainRow';
import Checkbox from './Checkbox';
import MobileQuestionsRow from './MobileQuestionsRow';
import { studentHasError } from 'utils/surveys';
import { isEqual } from 'lodash';

export default class ProgressSurveyRow extends React.Component {

  constructor() {
    super();
    this.state = { isExpanded: false };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (nextState.isExpanded !== this.state.isExpanded)
      || nextProps.isMobile !== this.props.isMobile 
      || !isEqual(nextProps.answers, this.props.answers)
      || !isEqual(nextProps.checkedQuestions, this.props.checkedQuestions)
      || !isEqual(nextProps.student, this.props.student);
  }

  componentDidUpdate(prevProps) {
    if ( this.hasError && !studentHasError(prevProps.checkedQuestions, prevProps.answers ) ) this.setState({isExpanded: true});
    else if ( !this.props.isMobile && this.state.isExpanded && !this.hasAtLeastOneChecked() ) this.setState({isExpanded: false});
  }

  onQuestionChecked(studentId, questionId, checked) {
    const questionIsRequired = this.props.surveyQuestions
      .find(questions => questions.id === questionId)
      .commentRequired;

    // Only expand on click if question is required
    if ( checked && questionIsRequired && !this.state.isExpanded ) this.setState({isExpanded: true}); 
    
    this.props.onQuestionChecked(studentId, questionId);
  }

  get availableQuestions() {
    return this.props.surveyQuestions.filter(({ type }) => type !== "default");
  }

  get hasError() {
    return studentHasError(this.props.checkedQuestions, this.props.answers);
  }
  
  isChecked(questionId) {
    const { checkedQuestions } = this.props;
    return checkedQuestions && checkedQuestions.indexOf(questionId) > -1 ? true : false;
  }
  
  hasAtLeastOneChecked() {
    return this.availableQuestions
      .reduce((hasAnyChecked, { id }) => hasAnyChecked || this.isChecked(id), false);
  }
  
  renderCheckbox(questionId, questionName) {
    return (
      <Checkbox
        checked={this.isChecked(questionId)}
        onChange={e => this.onQuestionChecked(this.props.student.id, questionId, e.target.checked)}
        questionName={questionName}
        studentId={this.props.student.id}
        studentName={this.props.student.name}
      />
    );
  }
  
  get desktopCommentsRow() {
    // Only renders when on desktop and the user has expanded the line
    if ( !this.state.isExpanded || this.props.isMobile ) return null;
    return (
      <DesktopCommentsRow
        studentId={this.props.student.id}
        studentName={this.props.student.name}
        numberOfQuestions={this.availableQuestions.length}
        checkedQuestions={this.availableQuestions.filter(({ id }) => this.isChecked(id))}
        onAnswerChanged={this.props.onAnswerChanged}
        answers={this.props.answers}
        hasError={this.hasError}
      />
    );
  }

  get mainRow() {
    // Always renders, but differs content from mobile to desktop
    return (
      <MainRow
        renderCheckbox={this.renderCheckbox.bind(this)}
        isExpanded={this.state.isExpanded}
        isChecked={this.isChecked.bind(this)}
        availableQuestions={this.availableQuestions}
        student={this.props.student}
        answers={this.props.answers}
        getStudentFolderLink={this.props.getStudentFolderLink}
        onToggleCommentsClick={() => this.setState({ isExpanded: !this.state.isExpanded })}
        showToggleButton={this.props.isMobile || this.hasAtLeastOneChecked()}
        onAnswerChanged={this.props.onAnswerChanged}
        isMobile={this.props.isMobile}
        hasError={this.hasError}
      />
    );
  }

  get mobileQuestionsRow() {
    // Only renders when on mobile and the user has expanded the line
    if ( !this.state.isExpanded || !this.props.isMobile ) return null;
    return (
      <MobileQuestionsRow
        renderCheckbox={this.renderCheckbox.bind(this)}
        studentId={this.props.student.id}
        studentName={this.props.student.name}
        isChecked={this.isChecked.bind(this)}
        availableQuestions={this.availableQuestions}
        onAnswerChanged={this.props.onAnswerChanged}
        answers={this.props.answers}
        hasError={this.hasError}
      />
    );
  }

  render() {
    return (
      <React.Fragment>
        {this.mainRow}
        {this.mobileQuestionsRow}
        {this.desktopCommentsRow}
      </React.Fragment>
    );
  }
}

ProgressSurveyRow.propTypes = {
  getStudentFolderLink: PropTypes.func.isRequired,
  student: ProgressSurveyAnswer.isRequired,
  surveyQuestions: PropTypes.arrayOf(ProgressSurveyQuestion),
  onQuestionChecked: PropTypes.func.isRequired,
  onAnswerChanged: PropTypes.func.isRequired,
  checkedQuestions: PropTypes.array,
  answers: PropTypes.array,
  isMobile: PropTypes.bool.isRequired,
};
